import React from 'react';
import { Flex, HStack, Checkbox, Text } from '@chakra-ui/react';
import FiltersBar from './FiltersBar';
import FilterSelect from './FilterSelect';
import DateRangePicker from './DateRangePicker';
import { getDateRangeFromPreset } from '../../utils/dateUtils';

// Filter options for different filter types
const FILTER_OPTIONS = {
	status: [
		{ value: 'passed', label: 'Passed' },
		{ value: 'failed', label: 'Failed' },
		{ value: 'skipped', label: 'Skipped' },
		{ value: 'flaky', label: 'Flaky' },
	],
	period: [
		{ value: 'daily', label: 'Daily' },
		{ value: 'weekly', label: 'Weekly' },
		{ value: 'monthly', label: 'Monthly' },
		{ value: 'quarterly', label: 'Quarterly' },
	],
	pageSize: [
		{ value: 10, label: '10 per page' },
		{ value: 20, label: '20 per page' },
		{ value: 50, label: '50 per page' },
		{ value: 100, label: '100 per page' },
	],
};

export default function ComprehensiveFilterBar({ 
	filters, 
	updateFilter, 
	resetFilters, 
	filterOptions = {},
	showAdvanced = false,
	showDateRange = true,
	showProject = true,
	showCompareTo = true,
	showStatus = true,
	showEnvironment = true,
	showBranch = true,
	showGroup = true,
	showUser = true,
	showPeriod = true,
	children,
	...props 
}) {
	const activeCount = Object.entries(filters).filter(([key, value]) => {
		if (key === 'page' || key === 'pageSize') return false;
		if (key === 'compareTo') return value === true;
		return value !== '' && value !== null && value !== undefined;
	}).length;

	return (
		<FiltersBar
			activeCount={activeCount}
			onReset={resetFilters}
			{...props}
		>
			{children}
			
			{/* Core Filters */}
			<HStack spacing={3} wrap="wrap">
				{/* Project Filter */}
				{showProject && filterOptions.projects && (
					<FilterSelect
						value={filters.project}
						onChange={(value) => updateFilter('project', value)}
						options={filterOptions.projects.map(p => ({ value: p.id || p, label: p.name || p }))}
						placeholder="All Projects"
					/>
				)}

				{/* Environment Filter */}
				{showEnvironment && filterOptions.environments && (
					<FilterSelect
						value={filters.environment}
						onChange={(value) => updateFilter('environment', value)}
						options={filterOptions.environments.map(e => ({ value: e, label: e }))}
						placeholder="All Environments"
					/>
				)}

				{/* Branch Filter */}
				{showBranch && filterOptions.branches && (
					<FilterSelect
						value={filters.branch}
						onChange={(value) => updateFilter('branch', value)}
						options={filterOptions.branches.map(b => ({ value: b, label: b }))}
						placeholder="All Branches"
					/>
				)}

				{/* Status Filter */}
				{showStatus && (
					<FilterSelect
						value={filters.status}
						onChange={(value) => updateFilter('status', value)}
						options={FILTER_OPTIONS.status}
						placeholder="All Statuses"
					/>
				)}

				{/* Group/Module Filter */}
				{showGroup && filterOptions.groups && (
					<FilterSelect
						value={filters.group}
						onChange={(value) => updateFilter('group', value)}
						options={filterOptions.groups.map(g => ({ value: g, label: g }))}
						placeholder="All Groups"
					/>
				)}

				{/* User Filter */}
				{showUser && filterOptions.users && (
					<FilterSelect
						value={filters.user}
						onChange={(value) => updateFilter('user', value)}
						options={filterOptions.users.map(u => ({ value: u.id || u, label: u.name || u }))}
						placeholder="All Users"
					/>
				)}

				{/* Period Filter */}
				{showPeriod && (
					<FilterSelect
						value={filters.period}
						onChange={(value) => updateFilter('period', value)}
						options={FILTER_OPTIONS.period}
						maxW="160px"
					/>
				)}

				{/* Date Range Picker */}
				{showDateRange && (
					<DateRangePicker
						preset={filters.dateRange}
						onPresetChange={(value) => {
							updateFilter('dateRange', value);
							if (value !== 'custom') {
								const { startDate, endDate } = getDateRangeFromPreset(value);
								updateFilter('startDate', startDate);
								updateFilter('endDate', endDate);
							}
						}}
						start={filters.startDate}
						end={filters.endDate}
						onStartChange={(value) => updateFilter('startDate', value)}
						onEndChange={(value) => updateFilter('endDate', value)}
					/>
				)}

				{/* Comparison Toggle */}
				{showCompareTo && (
					<Flex align="center" gap={2}>
						<Checkbox
							isChecked={filters.compareTo}
							onChange={(e) => updateFilter('compareTo', e.target.checked)}
							size="sm"
						/>
						<Text fontSize="sm" color="gray.600">Compare to Previous</Text>
					</Flex>
				)}
			</HStack>

			{/* Advanced Filters (shown when showAdvanced is true) */}
			{showAdvanced && (
				<HStack spacing={3} wrap="wrap" mt={3}>
					{/* Page Size */}
					<FilterSelect
						value={filters.pageSize}
						onChange={(value) => updateFilter('pageSize', parseInt(value))}
						options={FILTER_OPTIONS.pageSize}
						maxW="150px"
					/>
				</HStack>
			)}
		</FiltersBar>
	);
}
